import type { VKBridge } from '@vkontakte/vk-bridge'
import { isVkLaunch, vkLaunchParams } from './detect'

/** Public app identifier; the payment secret belongs only on the server. */
export const VK_APP_ID = '54834078'

type Bridge = Pick<VKBridge, 'send' | 'supportsAsync' | 'isEmbedded'>
type Method = Parameters<Bridge['supportsAsync']>[0]
export type VkCapabilities = { payments: boolean; rewarded: boolean }
export type VkOrderResult =
  | { status: 'pending'; providerOrderId: string }
  | { status: 'cancelled' | 'failed' | 'unknown' }
export type VkRewardResult = 'reward' | 'no-reward' | 'unavailable'

export class VkUnavailable extends Error {
  constructor() { super('Эта возможность VK сейчас недоступна') }
}
export class VkBusy extends Error {
  constructor() { super('Дождитесь закрытия окна VK') }
}

/** Bound only to this VK Mini App. Launch parameters are NOT authentication;
 * the server must independently verify them and supply monetization flags. */
export class VkPlatform {
  private initialization: Promise<void> | null = null
  private busy = false

  constructor(
    private readonly bridge: Bridge,
    private readonly launch: () => string,
    private readonly appId = VK_APP_ID,
  ) {}

  private isTarget(): boolean {
    const params = this.launch()
    return this.bridge.isEmbedded() && isVkLaunch(params)
      && new URLSearchParams(params).get('vk_app_id') === this.appId
  }

  async init(): Promise<void> {
    if (!this.isTarget()) throw new VkUnavailable()
    if (!this.initialization) {
      this.initialization = this.bridge.send('VKWebAppInit').then(response => {
        if (response.result !== true) throw new VkUnavailable()
      }).catch(e => {
        this.initialization = null
        throw e
      })
    }
    return this.initialization
  }

  private async supports(method: Method): Promise<boolean> {
    try { return await this.bridge.supportsAsync(method) === true } catch { return false }
  }

  /** Both server configuration and runtime SDK support are required. */
  async capabilities(server: VkCapabilities): Promise<VkCapabilities> {
    const unavailable = { payments: false, rewarded: false }
    if (!this.isTarget()) return unavailable
    try { await this.init() } catch { return unavailable }
    const [payments, checkAds, showAds] = await Promise.all([
      server.payments && this.supports('VKWebAppShowOrderBox'),
      server.rewarded && this.supports('VKWebAppCheckNativeAds'),
      server.rewarded && this.supports('VKWebAppShowNativeAds'),
    ])
    return { payments, rewarded: checkAds && showAds }
  }

  private async exclusive<T>(run: () => Promise<T>): Promise<T> {
    if (this.busy) throw new VkBusy()
    this.busy = true
    try { return await run() } finally { this.busy = false }
  }

  /** Call only on an explicit purchase action, with item from a server order.
   * A successful VK dialog is still pending server verification, never a grant.
   * No automatic retries/timeouts: an open payment dialog must not be duplicated. */
  purchase(item: string, enabled: boolean): Promise<VkOrderResult> {
    return this.exclusive(async () => {
      if (!item.trim() || !enabled) throw new VkUnavailable()
      if (!(await this.capabilities({ payments: enabled, rewarded: false })).payments) throw new VkUnavailable()
      try {
        const response = await this.bridge.send('VKWebAppShowOrderBox', { type: 'item', item })
        if (response.status === 'cancel') return { status: 'cancelled' }
        if (response.status === 'fail') return { status: 'failed' }
        if (response.status === 'success' && typeof response.order_id === 'string' && response.order_id.trim()) {
          return { status: 'pending', providerOrderId: response.order_id }
        }
        return { status: 'unknown' }
      } catch {
        // Rejection can be a lost result after payment. Recover the server order.
        return { status: 'unknown' }
      }
    })
  }

  /** Probe before reserving the server quota. This does not open an ad. */
  async rewardAvailable(enabled: boolean): Promise<boolean> {
    if (!(await this.capabilities({ payments: false, rewarded: enabled })).rewarded) return false
    try { return (await this.bridge.send('VKWebAppCheckNativeAds', { ad_format: 'reward' })).result === true } catch { return false }
  }

  /** Caller reserves a server session first and supplies a gameplay/audio pause
   * that returns its restoration function. Only reward permits server complete;
   * it is an untrusted SDK callback, not a cryptographic proof of viewing. */
  showReward(enabled: boolean, pause: () => () => void): Promise<VkRewardResult> {
    return this.exclusive(async () => {
      if (!await this.rewardAvailable(enabled)) return 'unavailable'
      const resume = pause()
      try {
        const response = await this.bridge.send('VKWebAppShowNativeAds', { ad_format: 'reward' })
        return response.result === true ? 'reward' : 'no-reward'
      } catch {
        return 'no-reward'
      } finally {
        resume()
      }
    })
  }
}

let instance: Promise<VkPlatform> | null = null
/** Load the official SDK only in a signed launch of the intended app. */
export function vkSdk(): Promise<VkPlatform> {
  const launch = vkLaunchParams()
  if (!isVkLaunch(launch) || new URLSearchParams(launch).get('vk_app_id') !== VK_APP_ID) {
    return Promise.reject(new VkUnavailable())
  }
  if (!instance) {
    instance = import('@vkontakte/vk-bridge').then(({ default: bridge }) => new VkPlatform(bridge, vkLaunchParams)).catch(e => {
      instance = null
      throw e
    })
  }
  return instance
}
